// dashboard/src/helpers/interactionMode.ts

/**
 * Determines if an interaction should be in "phone mode" (audio-only) based on the prompt file type.
 * 
 * Phone mode means:
 * - Show static audio indicator instead of video preview
 * - Hide recorded video playback
 * - Still records both audio and video internally (for technical consistency)
 * - Backend receives audio for analysis
 * 
 * Currently: Detects based on .mp3 file extension (matches iOS app behavior)
 * Future: Can be enhanced to check user preferences or other audio formats
 * 
 * @param promptUrl - The URL or path to the prompt media file
 * @returns true if phone mode should be used, false for video mode
 */
export const isPhoneMode = (promptUrl: string | null | undefined): boolean => {
  if (!promptUrl) return false;
  
  const urlWithoutParams = promptUrl.split('?')[0].toLowerCase();
  const isPhoneMode = urlWithoutParams.endsWith('.mp3');
  // console.log(`isPhoneMode: promptUrl=${promptUrl}, "urlWithoutParams=${urlWithoutParams}", isPhoneMode=${isPhoneMode}`);
  // Check for audio file extensions
  // Currently only mp3, but could expand to: .m4a, .aac, .wav, .ogg, etc.
  return isPhoneMode;
};

/**
 * Gets the media type from a file URL
 * @param fileUrl - The URL or path to the media file
 * @returns 'audio' | 'video' | 'unknown'
 */
export const getMediaType = (fileUrl: string | null | undefined): 'audio' | 'video' | 'unknown' => {
  if (!fileUrl) return 'unknown';
  
  // Remove query parameters
  const urlWithoutParams = fileUrl.split('?')[0].toLowerCase();
  
  // Audio formats
  if (urlWithoutParams.endsWith('.mp3') || urlWithoutParams.endsWith('.m4a') || 
      urlWithoutParams.endsWith('.aac') || urlWithoutParams.endsWith('.wav') || 
      urlWithoutParams.endsWith('.ogg')) {
    return 'audio';
  }
  
  // Video formats
  if (urlWithoutParams.endsWith('.mp4') || urlWithoutParams.endsWith('.webm') || 
      urlWithoutParams.endsWith('.mov') || urlWithoutParams.endsWith('.avi') || 
      urlWithoutParams.endsWith('.mkv')) {
    return 'video';
  }
  
  return 'unknown';
};

/**
 * Future enhancement: Determine recording mode based on multiple factors
 * This function can be expanded to include:
 * - User preference overrides
 * - Device capabilities
 * - Network conditions
 * 
 * @param promptUrl - The prompt media URL
 * @param userPreference - Optional user preference override
 * @returns 'audio' | 'video'
 */
export const getRecordingMode = (
  promptUrl: string | null | undefined,
  userPreference?: 'audio' | 'video' | null
): 'audio' | 'video' => {
  // Future: Check user preference first
  if (userPreference) {
    return userPreference;
  }
  
  // Default: match the prompt type
  return isPhoneMode(promptUrl) ? 'audio' : 'video';
};